import { Fragment, useEffect, useMemo, useState } from "react";
import { api, formatErr } from "../../../../lib/api";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { Textarea } from "../../../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../../components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../../../components/ui/dialog";
import { Plus, Save, Send, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Field, SectionCard } from "./shared";
import AccountSelect, { decodeAccountSelection, encodeLedgerSelection, useAccountOptions } from "./AccountSelect";
import VendorSelect from "./VendorSelect";
import { formatCurrency } from "./LineGrid";

/**
 * Phase 15 CORRECTION (Tally-like Purchase / Vendor Bill).
 *
 * Replaces the raw Debit/Credit journal grid that used to be the ONLY way
 * to enter a Vendor Bill (shared, via GridVoucherFormPage, with Journal/
 * Credit Note/Debit Note) with a business-friendly "Vendor + allocation
 * rows" entry, exactly like Tally's Purchase Voucher: the user only ever
 * picks a Vendor and one or more Expense/Asset ledgers with amounts - the
 * Vendor Payable credit line and the whole double-entry booking are always
 * built automatically here, never typed.
 *
 * This component is intentionally the ONLY place this new UI lives; it is
 * shared by exactly two callers:
 *   - VendorBillsPage.jsx (mode="create") - a brand new bill.
 *   - VoucherEditPage.jsx (mode="edit", voucherType === "PURCHASE" only) -
 *     editing an existing bill, reconstructing these same rows from its
 *     stored lines. Every other voucher type in VoucherEditPage keeps its
 *     original raw multi-line grid completely untouched.
 *
 * Per item 12 ("remove the raw Journal UI from Purchase Voucher"), this
 * component never renders a Debit/Credit column, a Party/Member/Vendor-
 * per-line field, or a Save Draft / Save & Post pair - there is exactly one
 * save action, and saving immediately creates (and, for a new bill, posts)
 * the accounting entry. No manual posting step exists for a Vendor Bill
 * any more.
 */

function blankAllocationRow() {
  return {
    key: Math.random().toString(36).slice(2),
    ledgerId: "", description: "", amount: "",
    assetName: "", assetQuantity: "", assetSerialNo: "", assetNotes: "", showAssetDetails: false,
  };
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

/** Reconstructs { vendorId, vendorLabel, rows } from an existing PURCHASE
 * voucher's stored lines - the credit line (vendorId set) identifies the
 * vendor, every debit line becomes one allocation row. Asset detail fields
 * (Phase 15 correction item 5) are carried straight through if present. */
function voucherToEditorState(voucher) {
  const lines = voucher?.lines || [];
  const creditLine = lines.find((l) => Number(l.credit) > 0);
  const debitLines = lines.filter((l) => Number(l.debit) > 0);
  return {
    vendorId: creditLine?.vendorId || "",
    vendorLabel: creditLine?.vendorLabel || "",
    rows: debitLines.length
      ? debitLines.map((l) => ({
          key: l.id || Math.random().toString(36).slice(2),
          ledgerId: l.ledgerId || "", description: l.narration || "", amount: l.debit || "",
          assetName: l.assetName || "", assetQuantity: l.assetQuantity || "", assetSerialNo: l.assetSerialNo || "",
          assetNotes: l.assetNotes || "", showAssetDetails: !!(l.assetName || l.assetQuantity || l.assetSerialNo || l.assetNotes),
        }))
      : [blankAllocationRow()],
  };
}

export default function VendorBillEditorForm({ mode, voucherId, initialVoucher, onSaved }) {
  const isEdit = mode === "edit";
  const accountOptions = useAccountOptions();
  const { ledgers } = accountOptions;

  const [date, setDate] = useState(initialVoucher?.voucherDate || todayIso());
  const [dueDate, setDueDate] = useState(initialVoucher?.dueDate || "");
  const [invoiceNo, setInvoiceNo] = useState(initialVoucher?.referenceNo || "");
  const [extraReference, setExtraReference] = useState(initialVoucher?.extraReference || "");
  const [narration, setNarration] = useState(initialVoucher?.narration || "");
  const [vendorId, setVendorId] = useState("");
  const [vendorLabel, setVendorLabel] = useState("");
  const [rows, setRows] = useState([blankAllocationRow()]);
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const [quickVendorOpen, setQuickVendorOpen] = useState(false);
  const [quickLedgerForRow, setQuickLedgerForRow] = useState(null); // row key, or null
  // A quick-created Vendor/Ledger (items 1 + 6 - "without leaving the
  // voucher") is not yet in VendorSelect's or useAccountOptions's own
  // one-time-fetched lists, so its name would otherwise show blank until
  // the page reloads. These two lists are merged into the relevant picker
  // immediately after creation so the just-created record displays and
  // behaves correctly right away.
  const [quickVendors, setQuickVendors] = useState([]);
  const [quickLedgers, setQuickLedgers] = useState([]);

  const mergedAccountOptions = useMemo(() => {
    if (!quickLedgers.length) return accountOptions;
    return {
      ...accountOptions,
      ledgers: [...accountOptions.ledgers, ...quickLedgers],
      otherLedgers: [...accountOptions.otherLedgers, ...quickLedgers],
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountOptions.ledgers, accountOptions.otherLedgers, accountOptions.loading, quickLedgers]);

  useEffect(() => {
    if (!isEdit || !initialVoucher) return;
    const state = voucherToEditorState(initialVoucher);
    setVendorId(state.vendorId);
    setVendorLabel(state.vendorLabel);
    setRows(state.rows);
    setDate(initialVoucher.voucherDate || todayIso());
    setDueDate(initialVoucher.dueDate || "");
    setInvoiceNo(initialVoucher.referenceNo || "");
    setExtraReference(initialVoucher.extraReference || "");
    setNarration(initialVoucher.narration || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, initialVoucher?.id]);

  const ledgerGroupById = useMemo(() => {
    const map = new Map();
    (ledgers || []).forEach((l) => map.set(l.id, l.ledgerGroup));
    quickLedgers.forEach((l) => map.set(l.id, l.ledgerGroup));
    return map;
  }, [ledgers, quickLedgers]);

  const updateRow = (key, patch) => setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const addRow = () => setRows((prev) => [...prev, blankAllocationRow()]);
  const removeRow = (key) => setRows((prev) => (prev.length <= 1 ? prev : prev.filter((r) => r.key !== key)));

  const validRows = rows.filter((r) => r.ledgerId && Number(r.amount) > 0);
  const subtotal = validRows.reduce((sum, r) => sum + Number(r.amount || 0), 0);
  const grandTotal = subtotal; // No separate Tax/Round-off feature exists yet in this engine - see item 4 note below.

  const uploadPendingAttachment = async (newVoucherId) => {
    if (!attachmentFile) return;
    try {
      const formData = new FormData();
      formData.append("entityType", "voucher");
      formData.append("entityId", newVoucherId);
      formData.append("label", attachmentFile.name || "Vendor Bill attachment");
      formData.append("file", attachmentFile);
      await api.post("/society/attachments", formData);
    } catch (err) {
      toast.error(formatErr(err, "Vendor Bill saved, but the attachment could not be uploaded"));
    }
  };

  const buildLines = () => {
    const allocationLines = validRows.map((r) => ({
      ledgerId: r.ledgerId, debit: Number(r.amount), narration: r.description || "",
      assetName: r.assetName || "", assetQuantity: r.assetQuantity || "", assetSerialNo: r.assetSerialNo || "", assetNotes: r.assetNotes || "",
    }));
    return [...allocationLines, { vendorId, credit: grandTotal }];
  };

  const save = async () => {
    if (!vendorId) { toast.error("Select a Vendor"); return; }
    if (!invoiceNo.trim()) { toast.error("Vendor Invoice No. is required"); return; }
    if (validRows.length === 0) { toast.error("Add at least one Expense / Asset row with a ledger and amount"); return; }
    try {
      setBusy(true);
      const lines = buildLines();
      const payload = {
        voucherType: "PURCHASE", voucherDate: date, referenceNo: invoiceNo.trim(),
        dueDate, extraReference, narration, lines,
      };
      if (isEdit) {
        await api.put(`/society/accounting/engine/vouchers/${voucherId}`, payload);
        // A leftover bill still in "draft" status (only possible from
        // before this correction removed Save Draft/Save & Post - a brand
        // new bill created after this correction is always posted
        // immediately) is posted right here too, so editing it can never
        // leave it silently stuck out of every balance/report.
        if (initialVoucher?.status === "draft") {
          await api.post(`/society/accounting/engine/vouchers/${voucherId}/post`);
          toast.success("Vendor Bill updated and posted");
        } else {
          toast.success("Vendor Bill updated - accounting entries recalculated immediately");
        }
        onSaved?.(voucherId);
      } else {
        const draft = await api.post("/society/accounting/engine/vouchers", payload);
        await api.post(`/society/accounting/engine/vouchers/${draft.data.id}/post`);
        await uploadPendingAttachment(draft.data.id);
        toast.success("Vendor Bill saved");
        // Reset for the next entry (create mode only).
        setVendorId(""); setVendorLabel(""); setInvoiceNo(""); setExtraReference(""); setNarration("");
        setDueDate(""); setDate(todayIso()); setRows([blankAllocationRow()]); setAttachmentFile(null);
        onSaved?.(draft.data.id);
      }
    } catch (err) {
      toast.error(formatErr(err, isEdit ? "Could not update this Vendor Bill" : "Could not save this Vendor Bill"));
    } finally {
      setBusy(false);
    }
  };

  const createQuickVendor = async (form) => {
    if (!form.vendorName.trim()) { toast.error("Vendor name is required"); return; }
    try {
      setBusy(true);
      // Item 1: "If vendor is created here, also create/link its vendor
      // payable ledger according to existing architecture" - mirrors
      // VendorsMasterPage's createVendor exactly (new dedicated ledger,
      // ledgerGroup "vendor", named "<Vendor> payable").
      const ledgerRes = await api.post("/society/accounting/engine/ledgers", {
        name: `${form.vendorName.trim()} payable`, ledgerGroup: "vendor", openingBalance: 0, openingSide: "credit",
      });
      const vendorRes = await api.post("/society/accounting/engine/vendors", {
        vendorName: form.vendorName.trim(), contactPerson: "", mobile: form.mobile.trim(), email: "",
        address: "", gstin: form.gstin.trim(), pan: "", paymentTerms: "",
        linkedLedgerId: ledgerRes.data.id, active: true, notes: "",
        openingBalance: 0, openingBalanceSide: "credit", openingAsOfDate: "",
      });
      setQuickVendors((prev) => [...prev, vendorRes.data]);
      setVendorId(vendorRes.data.id);
      setVendorLabel(vendorRes.data.vendorName);
      setQuickVendorOpen(false);
      toast.success(`Vendor "${vendorRes.data.vendorName}" created`);
    } catch (err) {
      toast.error(formatErr(err, "Could not create vendor"));
    } finally {
      setBusy(false);
    }
  };

  const createQuickLedger = async (form) => {
    if (!form.name.trim()) { toast.error("Ledger name is required"); return; }
    try {
      setBusy(true);
      // Item 6: "Do not allow creating incompatible control ledgers from
      // this popup" - the Type select below only ever offers "expense" or
      // "asset", never bank/cash/vendor/member/income/liability/fund/equity.
      const res = await api.post("/society/accounting/engine/ledgers", { name: form.name.trim(), ledgerGroup: form.ledgerGroup });
      setQuickLedgers((prev) => [...prev, res.data]);
      updateRow(quickLedgerForRow, { ledgerId: res.data.id });
      setQuickLedgerForRow(null);
      toast.success(`Ledger "${res.data.name}" created`);
    } catch (err) {
      toast.error(formatErr(err, "Could not create ledger"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5">
      <SectionCard title="Vendor Bill" subtitle="Business purchase entry - the accounting (Dr Expense/Asset, Cr Vendor Payable) is always built automatically below.">
        <div className="grid gap-3 md:grid-cols-3">
          <Field label="Date"><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} /></Field>
          <Field label="Vendor Invoice No. *">
            <Input value={invoiceNo} onChange={(e) => setInvoiceNo(e.target.value)} placeholder="e.g. INV-1042" />
          </Field>
          <Field label="Vendor *">
            <div className="flex gap-2">
              <div className="flex-1">
                <VendorSelect
                  value={vendorId}
                  onChange={setVendorId}
                  onSelectVendor={(v) => setVendorLabel(v?.vendorName || "")}
                  placeholder={vendorLabel || "Search vendor..."}
                  extraVendors={quickVendors}
                />
              </div>
              <Button type="button" variant="outline" size="sm" onClick={() => setQuickVendorOpen(true)}>
                <Plus className="h-3.5 w-3.5" />
              </Button>
            </div>
          </Field>
          <Field label="Due Date"><Input type="date" min={date} value={dueDate} onChange={(e) => setDueDate(e.target.value)} /></Field>
          <Field label="Reference (optional)">
            <Input value={extraReference} onChange={(e) => setExtraReference(e.target.value)} placeholder="PO / delivery note no." />
          </Field>
          {!isEdit && (
            <Field label="Attachment (optional)">
              <input
                type="file" accept="image/*,application/pdf"
                onChange={(e) => setAttachmentFile(e.target.files?.[0] || null)}
                className="block w-full text-sm text-[#374151] file:mr-3 file:rounded-sm file:border-0 file:bg-[#F3F4F6] file:px-3 file:py-1.5 file:text-xs file:font-semibold"
              />
            </Field>
          )}
          <div className="md:col-span-3">
            <Field label="Narration"><Textarea rows={2} value={narration} onChange={(e) => setNarration(e.target.value)} /></Field>
          </div>
        </div>
      </SectionCard>

      <SectionCard
        title="Expense / Asset Allocation"
        subtitle="Add one row per expense or fixed-asset ledger this bill covers. Bank, Cash, Vendor and Member ledgers are never offered here."
      >
        <div className="space-y-3">
          <div className="overflow-x-auto rounded-sm border border-[#E5E7EB]">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#E5E7EB] bg-[#F9FAFB] text-left text-xs uppercase tracking-[0.1em] text-[#6B7280]">
                  <th className="px-2 py-2">Ledger</th>
                  <th className="px-2 py-2">Description</th>
                  <th className="px-2 py-2 text-right">Amount</th>
                  <th className="px-2 py-2" />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const isAssetLedger = ledgerGroupById.get(row.ledgerId) === "asset";
                  return (
                    <Fragment key={row.key}>
                      <tr className="border-b border-[#F3F4F6]">
                        <td className="px-2 py-1.5 min-w-[220px]">
                          <div className="flex gap-2">
                            <div className="flex-1">
                              <AccountSelect
                                value={row.ledgerId ? encodeLedgerSelection(row.ledgerId) : ""}
                                onChange={(v) => updateRow(row.key, { ledgerId: decodeAccountSelection(v)?.ledgerId || "" })}
                                options={mergedAccountOptions}
                                hideCash hideBankAccounts hideVendors
                                context="purchaseAllocation"
                                placeholder="Search expense/asset ledger"
                                searchPlaceholder="Search expense or fixed-asset ledger..."
                              />
                            </div>
                            <Button type="button" variant="outline" size="sm" onClick={() => setQuickLedgerForRow(row.key)}>
                              <Plus className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                          {isAssetLedger && (
                            <button
                              type="button" className="mt-1 text-xs font-semibold text-[#111827] hover:underline"
                              onClick={() => updateRow(row.key, { showAssetDetails: !row.showAssetDetails })}
                            >
                              {row.showAssetDetails ? "Hide asset details" : "+ Add asset details (optional)"}
                            </button>
                          )}
                        </td>
                        <td className="px-2 py-1.5 min-w-[180px]">
                          <Input value={row.description} onChange={(e) => updateRow(row.key, { description: e.target.value })} placeholder="e.g. Electrical repairs - common area" />
                        </td>
                        <td className="px-2 py-1.5 w-32">
                          <Input type="number" min="0" step="0.01" value={row.amount} onChange={(e) => updateRow(row.key, { amount: e.target.value })} className="text-right" />
                        </td>
                        <td className="px-2 py-1.5 text-right">
                          <Button type="button" variant="outline" size="sm" disabled={rows.length <= 1} onClick={() => removeRow(row.key)}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </td>
                      </tr>
                      {isAssetLedger && row.showAssetDetails && (
                        <tr className="border-b border-[#F3F4F6] bg-[#F9FAFB]">
                          <td colSpan={4} className="px-2 py-2">
                            <div className="grid gap-2 sm:grid-cols-4">
                              <Input placeholder="Asset Name" value={row.assetName} onChange={(e) => updateRow(row.key, { assetName: e.target.value })} />
                              <Input placeholder="Quantity" value={row.assetQuantity} onChange={(e) => updateRow(row.key, { assetQuantity: e.target.value })} />
                              <Input placeholder="Serial No." value={row.assetSerialNo} onChange={(e) => updateRow(row.key, { assetSerialNo: e.target.value })} />
                              <Input placeholder="Notes" value={row.assetNotes} onChange={(e) => updateRow(row.key, { assetNotes: e.target.value })} />
                            </div>
                            <p className="mt-1 text-xs text-[#6B7280]">Optional, for reference only - this is not an inventory system and never affects the accounting entry.</p>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          <Button type="button" variant="outline" size="sm" onClick={addRow}><Plus className="mr-2 h-3.5 w-3.5" /> Add Expense / Asset Row</Button>

          <div className="flex flex-wrap justify-end gap-6 rounded-sm border border-[#E5E7EB] bg-[#F9FAFB] px-4 py-3 text-sm">
            <div>Subtotal: <span className="font-semibold text-[#111827]">{formatCurrency(subtotal)}</span></div>
            {/* Item 4: Tax / Other Charges and Round Off are not yet a
                separately-modeled feature in this accounting engine - add
                one as its own Expense/Asset row above if needed. Grand
                Total is always exactly the sum of the rows above, and is
                always what posts as the Vendor Payable credit. */}
            <div>Grand Total: <span className="font-bold text-[#111827]">{formatCurrency(grandTotal)}</span></div>
          </div>
        </div>
      </SectionCard>

      <div className="flex items-center gap-3">
        <Button type="button" disabled={busy} onClick={save} className="bg-[#111827] text-white hover:bg-[#374151]">
          {isEdit ? <><Save className="mr-2 h-4 w-4" /> Save changes</> : <><Send className="mr-2 h-4 w-4" /> SAVE VENDOR BILL</>}
        </Button>
        {!isEdit && <span className="text-xs text-[#6B7280]">Saving immediately books the accounting entry - there is no separate posting step.</span>}
      </div>

      {quickVendorOpen && <QuickVendorDialog busy={busy} onCancel={() => setQuickVendorOpen(false)} onCreate={createQuickVendor} />}
      {quickLedgerForRow && <QuickLedgerDialog busy={busy} onCancel={() => setQuickLedgerForRow(null)} onCreate={createQuickLedger} />}
    </div>
  );
}

function QuickVendorDialog({ busy, onCancel, onCreate }) {
  const [form, setForm] = useState({ vendorName: "", mobile: "", gstin: "" });
  return (
    <Dialog open onOpenChange={(v) => !v && onCancel()}>
      <DialogContent>
        <DialogHeader><DialogTitle>Create Vendor</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Field label="Vendor Name *"><Input value={form.vendorName} onChange={(e) => setForm((f) => ({ ...f, vendorName: e.target.value }))} placeholder="ABC Electricals" /></Field>
          <Field label="Mobile (optional)"><Input value={form.mobile} onChange={(e) => setForm((f) => ({ ...f, mobile: e.target.value }))} /></Field>
          <Field label="GSTIN (optional)"><Input value={form.gstin} onChange={(e) => setForm((f) => ({ ...f, gstin: e.target.value.toUpperCase() }))} /></Field>
          <p className="text-xs text-[#6B7280]">A dedicated payable ledger ("{form.vendorName || "Vendor"} payable") is created and linked automatically. Add GSTIN/PAN/address/opening balance later from Vendors master if needed.</p>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" disabled={busy} onClick={onCancel}>Cancel</Button>
          <Button type="button" disabled={busy || !form.vendorName.trim()} onClick={() => onCreate(form)} className="bg-[#111827] text-white hover:bg-[#374151]">Create Vendor</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function QuickLedgerDialog({ busy, onCancel, onCreate }) {
  const [form, setForm] = useState({ name: "", ledgerGroup: "expense" });
  return (
    <Dialog open onOpenChange={(v) => !v && onCancel()}>
      <DialogContent>
        <DialogHeader><DialogTitle>Create Ledger</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Field label="Ledger Name *"><Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. Security Expense" /></Field>
          <Field label="Type *">
            <Select value={form.ledgerGroup} onValueChange={(v) => setForm((f) => ({ ...f, ledgerGroup: v }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="expense">Expense (running cost - Repairs, Security, Electricity, Professional Fees, Housekeeping...)</SelectItem>
                <SelectItem value="asset">Fixed Asset / Purchase-Consumables (Furniture, Computers, Equipment, Stores...)</SelectItem>
              </SelectContent>
            </Select>
          </Field>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" disabled={busy} onClick={onCancel}>Cancel</Button>
          <Button type="button" disabled={busy || !form.name.trim()} onClick={() => onCreate(form)} className="bg-[#111827] text-white hover:bg-[#374151]">Create Ledger</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
